import { z } from "zod";

import { invitationSchema, maybe, parser, type Parser } from "../live/schemas";
import {
  FOLDER_LEVELS,
  NOTIFICATION_KINDS,
  type AuditVerdict,
  type BulkInviteResult,
  type DeletedProject,
  type FolderGrant,
  type Inbox,
  type NotificationSettings,
  type ExportLink,
  type OrganisationExport,
  type ProjectInformation,
  type ProjectRules,
  type Team,
} from "./types";

/** The identity service's phase 2 replies, checked before pages see them; `null` becomes `undefined`. */

const deletedProject = z.object({
  id: z.string(),
  name: z.string(),
  client: z.string(),
  shortId: z.string(),
  deletedAt: z.string(),
  deletedBy: z.object({ id: z.string(), name: z.string() }),
  purgeAfter: z.string(),
  daysRemaining: z.number(),
});

const rules = z.object({ recoveryDays: z.number(), numberPattern: z.string() });

const information = z.object({
  projectNumber: z.string(),
  name: z.string(),
  client: z.string(),
  ownerName: maybe(z.string()),
  siteAddress: maybe(z.string()),
  city: z.string(),
  ward: maybe(z.string()),
  state: z.string(),
  plots: z.array(z.object({ name: z.string(), identifiers: z.unknown() })),
  architectOfRecord: z.object({ name: maybe(z.string()), coaNumber: maybe(z.string()) }),
  issueDate: maybe(z.string()),
  updatedAt: maybe(z.string()),
  updatedBy: maybe(z.string()),
});

const setting = z.object({
  kind: z.enum(NOTIFICATION_KINDS),
  inApp: z.boolean(),
  email: z.boolean(),
});

const settings = z.object({ settings: z.array(setting), own: maybe(z.boolean()) });

const inbox = z.object({
  notifications: z.array(
    z.object({
      id: z.string(),
      projectId: maybe(z.string()),
      kind: z.string(),
      title: z.string(),
      body: z.string(),
      link: maybe(z.string()),
      actorId: maybe(z.string()),
      createdAt: z.string(),
      read: z.boolean(),
    }),
  ),
  unread: z.number(),
});

const bulk = z.object({
  sent: z.number(),
  invitations: z.array(invitationSchema),
  refused: z.array(z.object({ row: z.number(), email: z.string(), reason: z.string() })),
});

const team = z.object({
  id: z.string(),
  name: z.string(),
  handle: z.string(),
  members: z.array(z.object({ userId: z.string(), teamAdmin: z.boolean() })),
  projects: z.array(z.object({ projectId: z.string(), role: z.string() })),
});

const grant = z.object({
  kind: z.enum(["person", "team"]),
  id: z.string(),
  level: z.enum(FOLDER_LEVELS),
});

const verdict = z.object({
  ok: z.boolean(),
  checked: z.number(),
  unchained: z.number(),
  brokenAt: maybe(z.string()),
});

const exportProgress = z
  .object({ stage: z.string(), done: z.number(), total: z.number() })
  .optional()
  .catch(undefined);

const organisationExport = z.object({
  id: z.string(),
  status: z.enum(["queued", "running", "ready", "failed"]),
  requestedBy: z.string(),
  createdAt: z.string(),
  finishedAt: maybe(z.string()),
  sizeBytes: maybe(z.number()),
  expiresAt: maybe(z.string()),
  error: maybe(z.string()),
  progress: exportProgress,
  parts: z
    .array(
      z.object({
        name: z.string(),
        bytes: z.number(),
        sha256: z.string(),
        project: maybe(z.string()),
      }),
    )
    .default([]),
});

const exportLink = z.object({ url: z.string(), expiresAt: z.string(), name: z.string() });

export const parseDeleted: Parser<DeletedProject[]> = parser(z.array(deletedProject));
export const parseRules: Parser<ProjectRules> = parser(rules);
export const parseInformation: Parser<ProjectInformation> = parser(information);
export const parseSettings: Parser<NotificationSettings> = parser(settings);
export const parseInbox: Parser<Inbox> = parser(inbox);
export const parseBulk: Parser<BulkInviteResult> = parser(bulk);
export const parseTeam: Parser<Team> = parser(team);
export const parseTeams: Parser<Team[]> = parser(z.array(team));
export const parseGrants: Parser<FolderGrant[]> = parser(
  z.object({ grants: z.array(grant) }).transform((reply) => reply.grants),
);
export const parseVerdict: Parser<AuditVerdict> = parser(verdict);
export const parseExport: Parser<OrganisationExport> = parser(organisationExport);
export const parseExports: Parser<OrganisationExport[]> = parser(z.array(organisationExport));
export const parseExportLink: Parser<ExportLink> = parser(exportLink);
export const parseAi: Parser<boolean> = parser(
  z.object({ aiEnabled: z.boolean() }).transform((reply) => reply.aiEnabled),
);
export const parseSignOut: Parser<{ sessionsEnded: number }> = parser(
  z.object({ sessionsEnded: z.number() }),
);
export const parseFactorReset: Parser<{ factorsRemoved: number; sessionsEnded: number }> = parser(
  z.object({ factorsRemoved: z.number(), sessionsEnded: z.number() }),
);
/** Any reply: for calls whose body the page does not read. */
export const ignoreBody: Parser<undefined> = { parse: () => undefined };
